import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 定数 ---

/** ワークスペースのノード種別 */
export const WorkSpaceType = {
  FOLDER: 'フォルダ',
  FILE: 'ファイル',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type WorkSpaceType = keyof typeof WorkSpaceType;

// --- 型定義 ---

/** ワークスペースのノード（フラット配列の1要素） */
export type WorkSpaceNode = {
  id: string;
  parentId: string | null;
  type: WorkSpaceType;
  name: string;
  fileId: string | null;
  /** ルートからの絶対パス（例: /src/Main.java）。サーバが都度計算（API6101/6109 v2） */
  path?: string;
};

export type CreateFolderParams = {
  parentId?: string | null;
  name: string;
};

export type CreateFileParams = {
  parentId?: string | null;
  name: string;
  /** 初期テキスト（テンプレート）。拡張子マスタ(API6110)の template を渡す。未指定/nullは空文字保存（API6103 v2） */
  initialDetail?: string;
};

export type MoveNodeParams = {
  /** 移動対象の work_space.id（必須, 最大64文字） */
  id: string;
  /** 新しい親フォルダの work_space.id。ルート直下へ移動する場合は null / 未指定（最大64文字） */
  newParentId?: string | null;
};

export type RenameNodeParams = {
  id: string;
  name: string;
};

export type DeleteNodeParams = {
  id: string;
};

export type SaveFileParams = {
  fileId: string;
  detail: string;
  version: number;
  // --- 記事同期用（任意・レガシー）。
  // R-11 で保存/反映分離に統一され、保存（API6107 v3）は純粋な下書き保存になった。
  // 記事メタの同期は記事反映API（API6211・reflectArticle）に分離したため、
  // 保存時にこれらを送らない（型は後方互換のため残置）。 ---
  title?: string;
  subtitle?: string;
  description?: string;
  visibility?: string;
  authorUserIds?: string[];
};

export type RevertFileParams = {
  fileId: string;
  seq: number;
  version: number;
};

/** ファイル詳細（ファイルを開いた時の内容） */
export type FileDetailResult = {
  fileId: string;
  /** ファイル名（work_space.name） */
  name: string;
  /** ルートからの絶対パス（例: /src/Main.java）。サーバが都度計算（API6106 v4） */
  path: string;
  extension: string;
  detail: string;
  languageId: number | null;
  version: number;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  updatedAt: string;
  updatedBy: string;
  updatedByName: string;
  /** その fileId を参照する自分（created_by 本人）の記事IDリスト（API6106 改修）。記事化されていなければ空配列 */
  articleIds: string[];
  /**
   * その fileId が設問knzとして利用されている場合の設問ID（API6106 v5）。
   * 設問化されていなければ null。null でなければ workspace で `[設問に反映]` を表示する。
   */
  questionId: string | null;
  /** 本文文字数（readTime 換算用。NULL 許可） */
  charCount: number | null;
  /** そのファイルの最後の実行（seq最大）の実行時引数（半角スペース区切り）。実行履歴がなければ null（API6106 v4） */
  lastArguments: string | null;
};

/** 拡張子マスタ */
export type FileExtensionResult = {
  extension: string;
  /** language_id の数値文字列。実行不可(md 等)は null */
  code: string | null;
  editorLanguage: string;
  label: string;
  /** ファイル作成時の初期テキスト（実行可能拡張子のみ。md/knz は null）（API6110 v2） */
  template: string | null;
};

const sessionHeaders = () => ({
  headers: {
    'X-Session-Id': localStorage.getItem('session-id'),
  },
});

// --- API関数 ---

/**
 * ワークスペース取得API（本人）
 * ログインユーザー本人のワークスペースツリー全体を取得する。0件でも空配列。
 */
export const getWorkSpace = async (): Promise<WorkSpaceNode[]> => {
  try {
    const response = await axios.get(KanzhongBackend.GET_WORKSPACE, sessionHeaders());
    return response.data.nodes ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ワークスペース取得（管理者）API
 * 指定したユーザーのワークスペースツリー全体を取得する。DEVELOPER / ADMIN のみ。
 * @param userId - 取得対象ユーザーの user_id（必須, 最大300文字）
 */
export const getAdminWorkSpace = async (userId: string): Promise<WorkSpaceNode[]> => {
  try {
    const response = await axios.post(
      KanzhongBackend.GET_ADMIN_WORKSPACE,
      { userId },
      sessionHeaders(),
    );
    return response.data.nodes ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * フォルダ作成API
 * @param params.parentId - 親フォルダの id（任意, 最大64文字, ルート直下は null / 未指定）
 * @param params.name - フォルダ名（必須, 最大255文字, 前後空白はトリム）
 * @returns 作成した work_space.id
 */
export const createFolder = async (params: CreateFolderParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.CREATE_WORKSPACE_FOLDER,
      params,
      sessionHeaders(),
    );
    return response.data.id;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ファイル作成API
 * work_space（type=FILE）と file_detail を同時登録する。
 * @param params.parentId - 親フォルダの id（任意, 最大64文字, ルート直下は null / 未指定）
 * @param params.name - ファイル名（必須, 最大255文字, 拡張子込み。作成可能拡張子はマスタに準拠）
 * @param params.initialDetail - 初期テキスト（任意, テンプレート。未指定/nullは空文字保存）
 * @returns 作成した work_space.id
 */
export const createFile = async (params: CreateFileParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.CREATE_WORKSPACE_FILE,
      params,
      sessionHeaders(),
    );
    return response.data.id;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ノード名変更API（フォルダ/ファイル共通）
 * @param params.id - 対象ノードの work_space.id（必須, 最大64文字）
 * @param params.name - 新しい名前（必須, 最大255文字, ファイルの拡張子変更は不可）
 * @returns 対象の id
 */
export const renameNode = async (params: RenameNodeParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.RENAME_WORKSPACE_NODE,
      params,
      sessionHeaders(),
    );
    return response.data.id;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ノード削除API（フォルダ/ファイル共通・物理削除）
 * フォルダは配下に子があれば422。ファイルは file_detail・実行履歴も削除。
 * @param params.id - 対象ノードの work_space.id（必須, 最大64文字）
 * @returns 削除した id
 */
export const deleteNode = async (params: DeleteNodeParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.DELETE_WORKSPACE_NODE,
      params,
      sessionHeaders(),
    );
    return response.data.id;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ノード移動API（API6112）
 * フォルダ/ファイルの親（parent_id）を変更して移動する。フォルダは配下ごと移動する。
 * サーバ側で所有者/移動先種別/ツリー整合/循環/同名 の各チェックを行う（違反は 422）。
 * 冪等（現在と同じ親）の場合は更新せず成功する。
 * @param params.id - 移動対象の work_space.id（必須, 最大64文字）
 * @param params.newParentId - 新親フォルダの work_space.id（任意, 最大64文字, ルート直下は null / 未指定）
 * @returns 移動した work_space.id
 */
export const moveNode = async (params: MoveNodeParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.MOVE_WORKSPACE_NODE,
      params,
      sessionHeaders(),
    );
    return response.data.id;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ファイル詳細取得API（API6106）
 * ファイルの内容（file_detail）を取得する。
 * レスポンスには絶対パス（path）・前回実行引数（lastArguments）も含む（v4）。
 * @param fileId - 対象ファイルの file_id
 */
export const getFileDetail = async (fileId: string): Promise<FileDetailResult> => {
  try {
    const response = await axios.get(
      `${KanzhongBackend.GET_WORKSPACE_FILE}/${encodeURIComponent(fileId)}`,
      sessionHeaders(),
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ファイル保存API（楽観ロックあり）
 * @param params.fileId - 対象ファイル（必須, 最大64文字）
 * @param params.detail - ファイル内容（任意, null / 空文字を許容）
 * @param params.version - 楽観ロック用バージョン（必須）
 * @param params.title - 記事同期用タイトル（任意）
 * @param params.subtitle - 記事同期用サブタイトル（任意）
 * @param params.description - 記事同期用説明（任意）
 * @param params.visibility - 記事同期用公開設定（任意, PUBLIC / INTERNAL / PRIVATE）
 * @param params.authorUserIds - 記事同期用著者（任意）
 * @returns 対象の fileId
 */
export const saveFile = async (params: SaveFileParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.SAVE_WORKSPACE_FILE,
      params,
      sessionHeaders(),
    );
    return response.data.fileId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ファイルリバートAPI（楽観ロックあり）
 * 指定した実行結果の内容で file_detail.detail を上書きする。
 * @param params.fileId - 対象ファイル（必須, 最大64文字）
 * @param params.seq - 戻したい実行結果の連番（必須）
 * @param params.version - 楽観ロック用バージョン（必須）
 * @returns 対象の fileId
 */
export const revertFile = async (params: RevertFileParams): Promise<string> => {
  try {
    const response = await axios.post(
      KanzhongBackend.REVERT_WORKSPACE_FILE,
      params,
      sessionHeaders(),
    );
    return response.data.fileId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 拡張子マスタ取得API
 * 作成可能な拡張子・language_id（code）・エディタ言語・表示名の一覧を取得する。sort_order 昇順。
 */
export const listFileExtension = async (): Promise<FileExtensionResult[]> => {
  try {
    const response = await axios.get(KanzhongBackend.LIST_FILE_EXTENSION, sessionHeaders());
    return response.data.extensions ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
